import type { QueryClient } from '@tanstack/react-query';

import { logger } from '@/core/logger';
import { refreshAmenityCatalog } from '@/core/services/amenityCatalogService';
import { clearPropertyAssetsCache, refreshLoginConfig } from '@/core/services/contentfulClient';
import { refreshContentfulTranslations } from '@/core/services/translationService';

type Locale = 'en' | 'ar';

/** React Query keys whose data comes from Contentful. */
const CONTENTFUL_QUERY_KEYS = [
  ['ventures'],
  ['venture'],
  ['faqs'],
  ['guest', 'contentful-assets'],
] as const;

/**
 * Bumped after every app-wide Contentful refresh. Screens that load Contentful
 * in their own effects add it to the effect deps (useContentfulRevision), so an
 * open screen reloads its CMS content instead of waiting for the next visit.
 */
let contentfulRevision = 0;
const revisionListeners = new Set<() => void>();

export function getContentfulRevision(): number {
  return contentfulRevision;
}

export function subscribeContentfulRevision(listener: () => void): () => void {
  revisionListeners.add(listener);
  return () => {
    revisionListeners.delete(listener);
  };
}

function bumpContentfulRevision(): void {
  contentfulRevision += 1;
  revisionListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore subscriber errors so one bad listener cannot block others
    }
  });
}

/**
 * Re-check every piece of Contentful data the app keeps in memory:
 * - translations + CMS config (e.g. Emirates ID OTP resend seconds) and ADHA copy
 * - login config (UAE PASS / Emirates ID buttons)
 * - amenity catalog
 * - project / unit assets: cache dropped; open screens reload them through
 *   the revision signal below
 * - React Query data from Contentful (ventures, FAQs): mounted screens refetch
 * Each part fails on its own; whatever is already on screen stays.
 */
export async function refreshAllContentful(
  locale: Locale,
  queryClient: QueryClient,
): Promise<void> {
  // Translations go first and double as a reachability check. If Contentful
  // cannot be reached (offline, outage), stop here: clearing caches and telling
  // screens to reload would only replace what is on screen with empty/fallback
  // content.
  const isReachable = await refreshContentfulTranslations(locale);
  if (!isReachable) {
    logger.info('contentfulRefreshService', 'Contentful unreachable, keeping current content');
    return;
  }
  clearPropertyAssetsCache();
  const results = await Promise.allSettled([
    refreshLoginConfig(),
    refreshAmenityCatalog(locale),
    ...CONTENTFUL_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  ]);
  const failed = results.filter((result) => result.status === 'rejected').length;
  if (failed > 0) {
    logger.warn('contentfulRefreshService', 'Some Contentful refreshes failed', { failed });
  }
  // Asset cache is already cleared, so screens reloading now get fresh content.
  bumpContentfulRevision();
}
